import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_africa-ventures/artifacts/mjqy16nm_image.png";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      {/* Header */}
      <header className="border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/">
              <img src={LOGO_URL} alt="Berkshire Africa" className="h-10" />
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="font-heading text-4xl font-bold text-slate-900 mb-8">Terms of Service</h1>
        
        <div className="prose prose-slate max-w-none">
          <p className="text-lg text-slate-600 mb-8">
            Last updated: March 2025
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-600 mb-4">
              By accessing or using the Berkshire Africa Inc. website and investment platform ("Services"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">2. Description of Services</h2>
            <p className="text-slate-600 mb-4">
              Berkshire Africa Inc. is a Pan-African investment holding company that provides:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Information about our investment portfolio and strategy</li>
              <li>LP (Limited Partner) investment opportunities</li>
              <li>Portfolio management tools for authorized investors</li>
              <li>Deal pipeline tracking for team members</li>
              <li>Analytics and reporting for stakeholders</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">3. Eligibility</h2>
            <p className="text-slate-600 mb-4">
              To use our investment services, you must:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Be at least 18 years of age</li>
              <li>Have the legal capacity to enter into binding agreements</li>
              <li>Meet applicable accredited investor requirements for investment opportunities</li>
              <li>Provide accurate and complete registration information</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">4. Investment Disclaimer</h2>
            <div className="bg-amber-50 border border-amber-200 p-6 rounded-lg mb-4">
              <p className="text-slate-700 font-medium mb-2">Important Notice:</p>
              <p className="text-slate-600">
                All investments involve risk, including the possible loss of principal. Past performance does not guarantee future results. The information provided on this platform is for informational purposes only and does not constitute investment advice, financial advice, or any other type of advice. You should consult with qualified professionals before making any investment decisions.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">5. User Accounts</h2>
            <p className="text-slate-600 mb-4">
              When you create an account, you agree to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security and confidentiality of your login credentials</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">6. Confidentiality</h2>
            <p className="text-slate-600 mb-4">
              Information shared through our platform, including but not limited to portfolio data, investment terms, deal flow information, and financial projections, is confidential and proprietary. You agree not to disclose, share, or reproduce such information without our prior written consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">7. Intellectual Property</h2>
            <p className="text-slate-600 mb-4">
              All content on this platform, including text, graphics, logos, images, and software, is the property of Berkshire Africa Inc. or its licensors and is protected by intellectual property laws. You may not copy, modify, distribute, or create derivative works without our express permission.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-slate-600 mb-4">
              To the maximum extent permitted by law, Berkshire Africa Inc. shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our Services, including but not limited to investment losses, loss of data, or business interruption.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">9. Governing Law</h2>
            <p className="text-slate-600 mb-4">
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Berkshire Africa Inc. is incorporated, without regard to conflict of law principles.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">10. Changes to Terms</h2>
            <p className="text-slate-600 mb-4">
              We reserve the right to modify these Terms at any time. We will notify users of material changes by posting the updated terms on our website. Your continued use of the Services after such modifications constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">11. Contact Us</h2>
            <p className="text-slate-600 mb-4">
              If you have questions about these Terms of Service, please contact us at:
            </p>
            <div className="bg-slate-50 p-6 rounded-lg">
              <p className="text-slate-900 font-semibold">Berkshire Africa Inc.</p>
              <p className="text-slate-600">Email: invest@berkshireafrica.com</p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 bg-slate-900 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-400 text-sm">
            © 2025 Berkshire Africa Inc. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
